import { 
  faUser, 
  faInfoCircle, 
  faEdit, 
  faSignature, 
  faEnvelope, 
  faPhone, 
  faIdCard,
  faBuilding,
  faCheckCircle,
  faExclamationTriangle,
  faKeyboard,
  faUpload,
  faPencilAlt,
  faImage,
  faCrop,
  faUndo,
  faRedo,
  faEye,
  faStar,
  faBell,
  faShieldAlt,
  faKey,
  faLock
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Alert,
  OverlayTrigger,
  Tooltip,
  Badge,
  Container,
  Nav,
  Tab
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { LOGIN } from "../redux/actions/actionTypes";
import ProfileUpdatePassword from "./ProfileUpdatePassword";
import NotificationPreferences from "./NotificationPreferences";
import MFASetupModal from "./MFASetupModal";
import { postApi } from "../redux/api";
import SignaturePad from "react-signature-canvas";
import { toast } from "react-hot-toast";
import { confirmDialog } from "./ConfirmDialog";
import { useNavigate, useSearchParams } from "react-router-dom";

export default function Profile() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const forceProfile = searchParams.get("forceProfile") === "true";
  const emailInputRef = useRef();
  const [form, setForm] = useState({
    fullname: user?.fullname || "",
    username: user?.username || "",
    email: user?.email || "",
    phone: user?.phone || "",
    designation: user?.designation || "",
    mda_code: user?.mda_code || "",
    old_password: "",
    new_password: "",
    comfirm_password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showHelpInfo, setShowHelpInfo] = useState(false);
  const toggle = () => setOpen(!open);
  const [imageURL, setImageURL] = useState(user?.signature || null);
  const [errors, setErrors] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showMfaModal, setShowMfaModal] = useState(false);
  const [resettingMfa, setResettingMfa] = useState(false);

  const handleDisableMfa = async () => {
    if (!(await confirmDialog("Are you sure you want to disable Two-Factor Authentication (2FA)? Your account will rely on email/SMS verification.", { title: "Disable 2FA", confirmText: "Disable" }))) {
      return;
    }
    setResettingMfa(true);
    try {
      const resp = await postApi("mfa/reset", { username: user.username });
      if (resp.success) {
        dispatch({
          type: LOGIN,
          payload: { ...user, totp_enabled: 0, mfa_enabled: 0 },
        });
        toast.success("2FA has been disabled for your account.");
      } else {
        toast.error(resp.msg || "Failed to disable 2FA");
      }
    } catch {
      toast.error("Failed to disable 2FA");
    } finally {
      setResettingMfa(false);
    }
  };
  
  // Signature creation states
  const [activeSignatureTab, setActiveSignatureTab] = useState("draw");
  const [typedSignature, setTypedSignature] = useState({
    text: user?.fullname || "",
    font: "Dancing Script",
    size: 24,
    style: "normal"
  });
  const [uploadedImage, setUploadedImage] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [backgroundRemovalStrength, setBackgroundRemovalStrength] = useState(40);
  const [showAdvancedUpload, setShowAdvancedUpload] = useState(false);
  const fileInputRef = useRef(null);

  // Available signature fonts
  const signatureFonts = [
    { name: "Dancing Script", value: "Dancing Script", style: "cursive" },
    { name: "Great Vibes", value: "Great Vibes", style: "cursive" },
    { name: "Allura", value: "Allura", style: "cursive" },
    { name: "Alex Brush", value: "Alex Brush", style: "cursive" },
    { name: "Satisfy", value: "Satisfy", style: "cursive" },
    { name: "Kaushan Script", value: "Kaushan Script", style: "cursive" }
  ];

  // Helper function to create tooltips
  const renderTooltip = (text) => (
    <Tooltip id="tooltip">{text}</Tooltip>
  );

  const handleChange = ({ target: { name, value } }) => {
    setForm((p) => ({ ...p, [name]: value }));
    setHasUnsavedChanges(true);
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!form.fullname.trim()) {
      newErrors.fullname = "Full name is required";
    }
    
    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = "Email is invalid";
    }
    
    if (!form.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^0\d{10}$/.test(form.phone)) {
      newErrors.phone = "Enter a valid Nigerian phone number (11 digits starting with 0)";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fix the errors highlighted in red below");
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
        }
      }
      return;
    }

    setIsLoading(true);

    const data = {
      email: form.email,
      phone: form.phone,
      username: form.username,
      fullname: form.fullname,
      signature: imageURL,
      designation: form.designation,
    };

    postApi("treasury-app/update_contact_info", data)
      .then((resp) => {
        if (resp.success) {
          toast.success("Your profile has been updated successfully!");
          setShowSuccessAlert(true);
          setHasUnsavedChanges(false);
          setTimeout(() => setShowSuccessAlert(false), 5000);
          if (forceProfile) {
            navigate("/");
          }
        } else {
          toast.error(resp.msg || "Failed to update profile. Please try again.");
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error("Something went wrong. Please check your internet connection and try again.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const sigCanvas = useRef({});
  const clear = () => sigCanvas.current.clear();
  
  // Preview drawn signature with white background
  const previewDrawnSignature = () => {
    if (sigCanvas.current.isEmpty()) {
      setSignaturePreview(null);
      return;
    }
    
    // Get the trimmed canvas from signature pad
    const originalCanvas = sigCanvas.current.getTrimmedCanvas();
    
    // Create a new canvas with white background
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Set canvas dimensions to match the original
    canvas.width = originalCanvas.width;
    canvas.height = originalCanvas.height;
    
    // Fill with white background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw the signature on top of the white background
    ctx.drawImage(originalCanvas, 0, 0);
    
    // Set preview
    setSignaturePreview(canvas.toDataURL("image/jpeg", 0.8));
  };
  
  // Generate typed signature preview with compression
  const generateTypedSignature = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Use smaller canvas dimensions for better compression
    canvas.width = 300;
    canvas.height = 100;
    
    // Set background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Set font and text
    ctx.fillStyle = '#000';
    ctx.font = `${typedSignature.style === 'italic' ? 'italic ' : ''}${Math.min(typedSignature.size, 28)}px "${typedSignature.font}", cursive`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Draw text
    ctx.fillText(typedSignature.text, canvas.width / 2, canvas.height / 2);
    
    // Compress the image - use lower quality for smaller file size
    return canvas.toDataURL('image/jpeg', 0.7); // 70% quality JPEG instead of PNG
  };

  // Compress uploaded image with background removal
  const compressImage = (file, maxWidth = 300, maxHeight = 100, quality = 0.7, tolerance = null) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw original image
        ctx.drawImage(img, 0, 0, width, height);
        
        // Get image data for background removal
        const imageData = ctx.getImageData(0, 0, width, height);
        const data = imageData.data;
        
        // Background removal algorithm
        // Sample corner pixels to determine background color
        const corners = [
          [0, 0], [width-1, 0], [0, height-1], [width-1, height-1]
        ];
        
        let bgR = 0, bgG = 0, bgB = 0;
        let validCorners = 0;
        
        corners.forEach(([x, y]) => {
          const index = (y * width + x) * 4;
          if (index < data.length) {
            bgR += data[index];
            bgG += data[index + 1];
            bgB += data[index + 2];
            validCorners++;
          }
        });
        
        if (validCorners > 0) {
          bgR = Math.round(bgR / validCorners);
          bgG = Math.round(bgG / validCorners);
          bgB = Math.round(bgB / validCorners);
          
          // Remove background (make similar colors transparent)
          const removalTolerance = tolerance || backgroundRemovalStrength;
          
          for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            
            // Calculate color difference from background
            const diff = Math.sqrt(
              Math.pow(r - bgR, 2) + 
              Math.pow(g - bgG, 2) + 
              Math.pow(b - bgB, 2)
            );
            
            // If color is similar to background, make it transparent
            if (diff < removalTolerance) {
              data[i + 3] = 0; // Set alpha to 0 (transparent)
            }
            // Enhance dark colors (signature ink)
            else if (r < 150 && g < 150 && b < 150) {
              // Make dark colors darker and more opaque
              data[i] = Math.max(0, r - 20);     // Darken red
              data[i + 1] = Math.max(0, g - 20); // Darken green  
              data[i + 2] = Math.max(0, b - 20); // Darken blue
              data[i + 3] = 255;                 // Full opacity
            }
          }
          
          // Put the processed image data back
          ctx.putImageData(imageData, 0, 0);
        }
        
        // Convert to PNG to preserve transparency, but compress if too large
        let finalDataUrl = canvas.toDataURL('image/png');
        
        // If PNG is too large, fallback to JPEG with white background
        const pngSize = (finalDataUrl.length * 3) / 4 / 1024; // Size in KB
        if (pngSize > 200) { // If PNG > 200KB, use JPEG
          // Create new canvas with white background for JPEG
          const jpegCanvas = document.createElement('canvas');
          const jpegCtx = jpegCanvas.getContext('2d');
          jpegCanvas.width = width;
          jpegCanvas.height = height;
          
          // Fill with white background
          jpegCtx.fillStyle = 'white';
          jpegCtx.fillRect(0, 0, width, height);
          
          // Draw the processed image on top
          jpegCtx.drawImage(canvas, 0, 0);
          
          finalDataUrl = jpegCanvas.toDataURL('image/jpeg', quality);
        }
        
        resolve(finalDataUrl);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  // Reprocess uploaded image with new settings
  const reprocessImage = async () => {
    if (fileInputRef.current?.files?.[0]) {
      setUploadedImage('loading');
      try {
        const compressedImage = await compressImage(fileInputRef.current.files[0]);
        setUploadedImage(compressedImage);
        setSignaturePreview(compressedImage);
        toast.success('Image reprocessed with new settings!');
      } catch (error) {
        console.error('Error reprocessing image:', error);
        toast.error('Error reprocessing image. Please try again.');
      }
    }
  };

  // Handle file upload with compression
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        try {
          // Show loading state
          setUploadedImage('loading');
          
          // Compress the image
          const compressedImage = await compressImage(file);
          setUploadedImage(compressedImage);
          setSignaturePreview(compressedImage);
          
          toast.success('Image uploaded and optimized successfully!');
        } catch (error) {
          console.error('Error compressing image:', error);
          toast.error('Error processing image. Please try again.');
          setUploadedImage(null);
        }
      } else {
        toast.error('Please upload an image file (PNG, JPG, etc.)');
      }
    }
  };

  // Save signature based on active method with compression
  const save = () => {
    let signatureData = null;
    
    if (activeSignatureTab === "type") {
      if (!typedSignature.text.trim()) {
        toast.error("Please enter your name for the typed signature");
        return;
      }
      signatureData = generateTypedSignature();
    } else if (activeSignatureTab === "upload") {
      if (!uploadedImage || uploadedImage === 'loading') {
        toast.error("Please upload a signature image");
        return;
      }
      signatureData = uploadedImage;
    } else if (activeSignatureTab === "draw") {
      if (sigCanvas.current.isEmpty()) {
        toast.error("Please draw your signature before saving");
        return;
      }
      
      // Get the trimmed canvas from signature pad
      const originalCanvas = sigCanvas.current.getTrimmedCanvas();
      
      // Create a new canvas with white background for better visibility
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas dimensions to match the original
      canvas.width = originalCanvas.width;
      canvas.height = originalCanvas.height;
      
      // Fill with white background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw the signature on top of the white background
      ctx.drawImage(originalCanvas, 0, 0);
      
      // Convert to JPEG with compression for smaller file size
      signatureData = canvas.toDataURL("image/jpeg", 0.7);
    }
    
    // Check signature size before saving
    if (signatureData) {
      const sizeInBytes = (signatureData.length * 3) / 4; // Approximate base64 size
      const sizeInKB = sizeInBytes / 1024;
      
      if (sizeInKB > 500) { // If larger than 500KB, warn user
        toast.error(`Signature is too large (${Math.round(sizeInKB)}KB). Please try a different method or smaller image.`);
        return;
      }
      
      setImageURL(signatureData);
      setHasUnsavedChanges(true);
      toast.success(`Signature created successfully! (${Math.round(sizeInKB)}KB) Don't forget to save your profile.`);
      toggle();
    }
  };

  // Update typed signature preview
  useEffect(() => {
    if (activeSignatureTab === "type" && typedSignature.text.trim()) {
      setSignaturePreview(generateTypedSignature());
    }
  }, [typedSignature, activeSignatureTab]);

  useEffect(() => {
    if (user) {
      setForm({
        fullname: user.fullname || "",
        username: user.username || "",
        email: user.email || "",
        phone: user.phone || "",
        designation: user.designation || "",
        mda_code: user?.mda_code || "",
        old_password: "",
        new_password: "",
        comfirm_password: "",
      });
      setImageURL(user.signature || null);
      setTypedSignature(prev => ({
        ...prev,
        text: user.fullname || ""
      }));
    }
  }, [user]);

  return (
    <Container fluid className="px-0">
      <Card className="shadow-sm border-0" style={{ borderRadius: '12px' }}>
        <Card.Header className="bg-primary text-white" style={{ borderRadius: '12px 12px 0 0' }}>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <FontAwesomeIcon icon={faUser} className="me-3" size="lg" />
              <div>
                <h5 className="mb-0">My Profile</h5>
                <small className="opacity-75">Manage your personal information and preferences</small>
              </div>
            </div>
            <OverlayTrigger
              placement="left"
              overlay={renderTooltip("Click to show/hide helpful information")}
            >
              <Button
                variant="outline-light"
                size="sm"
                onClick={() => setShowHelpInfo(!showHelpInfo)}
              >
                <FontAwesomeIcon icon={faInfoCircle} />
              </Button>
            </OverlayTrigger>
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          <Tab.Container defaultActiveKey="profile">
            <Nav variant="tabs" className="px-4 pt-3">
              <Nav.Item>
                <Nav.Link eventKey="profile" className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faUser} className="me-2" />
                  Profile Information
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="notifications" className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faBell} className="me-2" />
                  Notification Preferences
                </Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content className="p-4">
              <Tab.Pane eventKey="profile">
                {forceProfile && (
                  <Alert variant="danger" className="mb-4">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    <strong>Profile Incomplete!</strong>
                    {" "}You must provide a valid email address and phone number before you can access other pages.
                    <ul className="mb-0 mt-2">
                      <li>Enter your <strong>email address</strong> and <strong>phone number</strong> below</li>
                      <li>Click <strong>"Save My Profile"</strong> to continue</li>
                    </ul>
                  </Alert>
                )}
                {showHelpInfo && (
                  <Alert variant="info" className="mb-4">
                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                    <strong>How to update your profile:</strong>
                    <ul className="mb-0 mt-2">
                      <li>Fill in your personal information in the fields below</li>
                      <li>Fields marked with a red star (*) are required</li>
                      <li>Create or update your digital signature for official documents</li>
                      <li>Click "Save My Profile" when you're done making changes</li>
                      <li>You can change your password in the section at the bottom</li>
                    </ul>
                  </Alert>
                )}

                {showSuccessAlert && (
                  <Alert variant="success" className="mb-4" dismissible onClose={() => setShowSuccessAlert(false)}>
                    <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                    <strong>Success!</strong> Your profile information has been updated successfully.
                  </Alert>
                )}

                {hasUnsavedChanges && (
                  <Alert variant="warning" className="mb-4">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    <strong>Unsaved Changes:</strong> You have made changes to your profile. Don't forget to click "Save My Profile" to keep your changes.
                  </Alert>
                )}

                <Form onSubmit={handleUpdate}>
            {/* Personal Information Section */}
            <div className="mb-4">
              <h6 className="text-primary mb-3 border-bottom pb-2">
                <FontAwesomeIcon icon={faIdCard} className="me-2" />
                Personal Information
              </h6>
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <FontAwesomeIcon icon={faUser} className="me-2 text-muted" />
                      Full Name 
                      <span className="text-danger ms-1">*</span>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("Enter your complete name as it appears on official documents")}
                      >
                        <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" size="sm" />
                      </OverlayTrigger>
                    </Form.Label>
                    <Form.Control
                      size="lg"
                      type="text"
                      value={form.fullname}
                      name="fullname"
                      onChange={handleChange}
                      placeholder="e.g., Musa Abubakar"
                      isInvalid={!!errors.fullname}
                      className="border-2"
                      style={{ borderRadius: '8px' }}
                    />
                    <Form.Control.Feedback type="invalid">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                      {errors.fullname}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      This name will appear on all your official documents
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <FontAwesomeIcon icon={faIdCard} className="me-2 text-muted" />
                      Username
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("Your username cannot be changed for security reasons")}
                      >
                        <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" size="sm" />
                      </OverlayTrigger>
                    </Form.Label>
                    <Form.Control
                      size="lg"
                      type="text"
                      value={form.username}
                      disabled
                      placeholder="Username (cannot be changed)"
                      className="bg-light border-2"
                      style={{ borderRadius: '8px' }}
                    />
                    <Form.Text className="text-muted">
                      Your username is permanent and cannot be modified
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <FontAwesomeIcon icon={faBuilding} className="me-2 text-muted" />
                      Ministry/Department/Agency
                    </Form.Label>
                    <Form.Control
                      size="lg"
                      type="text"
                      placeholder="Your organization"
                      value={user?.mda_name || "Not specified"}
                      disabled
                      className="bg-light border-2"
                      style={{ borderRadius: '8px' }}
                    />
                    <Form.Text className="text-muted">
                      This is set by your system administrator
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <FontAwesomeIcon icon={faIdCard} className="me-2 text-muted" />
                      Job Title/Designation
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("Enter your current job title or position")}
                      >
                        <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" size="sm" />
                      </OverlayTrigger>
                    </Form.Label>
                    <Form.Control
                      size="lg"
                      type="text"
                      placeholder="e.g., Accountant, Director, etc."
                      value={form.designation}
                      name="designation"
                      onChange={handleChange}
                      className="border-2"
                      style={{ borderRadius: '8px' }}
                    />
                    <Form.Text className="text-muted">
                      Your current position or job title
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Contact Information Section */}
            <div className="mb-4">
              <h6 className="text-primary mb-3 border-bottom pb-2">
                <FontAwesomeIcon icon={faPhone} className="me-2" />
                Contact Information
              </h6>
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <FontAwesomeIcon icon={faEnvelope} className="me-2 text-muted" />
                      Email Address 
                      <span className="text-danger ms-1">*</span>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("We'll use this email to send you important notifications")}
                      >
                        <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" size="sm" />
                      </OverlayTrigger>
                    </Form.Label>
                    <Form.Control
                      size="lg"
                      type="email"
                      ref={emailInputRef}
                      placeholder="e.g., musa.abubakar@example.com"
                      value={form.email}
                      name="email"
                      onChange={handleChange}
                      isInvalid={!!errors.email}
                      className="border-2"
                      style={{ borderRadius: '8px' }}
                    />
                    <Form.Control.Feedback type="invalid">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                      {errors.email}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      Important system notifications will be sent here
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <FontAwesomeIcon icon={faPhone} className="me-2 text-muted" />
                      Phone Number 
                      <span className="text-danger ms-1">*</span>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("Include country code if international (e.g., +234 for Nigeria)")}
                      >
                        <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" size="sm" />
                      </OverlayTrigger>
                    </Form.Label>
                    <Form.Control
                      size="lg"
                      type="tel"
                      placeholder="e.g., +234 801 234 5678"
                      value={form.phone}
                      name="phone"
                      onChange={handleChange}
                      isInvalid={!!errors.phone}
                      className="border-2"
                      style={{ borderRadius: '8px' }}
                    />
                    <Form.Control.Feedback type="invalid">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                      {errors.phone}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      For urgent communications and account security
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Digital Signature Section */}
            <div className="mb-4">
              <h6 className="text-primary mb-3 border-bottom pb-2">
                <FontAwesomeIcon icon={faSignature} className="me-2" />
                Digital Signature
              </h6>
              
              <Row className="mb-3">
                <Col md={8}>
                  <div className="d-flex align-items-start signature-actions">
                    <div className="me-3">
                      <Button
                        size="lg"
                        variant={imageURL ? "outline-primary" : "primary"}
                        onClick={toggle}
                        className="px-4"
                        style={{ borderRadius: '8px' }}
                      >
                        <FontAwesomeIcon icon={faSignature} className="me-2" />
                        {imageURL ? "Update My Signature" : "Create My Signature"}
                      </Button>
                      <div className="mt-2">
                        <small className="text-muted">
                          <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                          Your signature will appear on official documents
                        </small>
                      </div>
                    </div>
                    
                    {imageURL && (
                      <div className="ms-3">
                        <div className="mb-2">
                          <Badge bg="success" className="mb-2">
                            <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                            Signature Ready
                          </Badge>
                        </div>
                        <div className="border rounded p-2 bg-light" style={{ borderRadius: '8px' }}>
                          <img
                            src={imageURL}
                            alt="Your digital signature"
                            style={{
                              display: "block",
                              width: "200px",
                              height: "80px",
                              objectFit: "contain",
                              backgroundColor: "white",
                              borderRadius: "4px"
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </div>

            {/* Enhanced Signature Modal with 3 Options */}
            <Modal show={open} onHide={toggle} centered size="xl">
              <Modal.Header closeButton className="bg-primary text-white">
                <Modal.Title>
                  <FontAwesomeIcon icon={faSignature} className="me-2" />
                  Create Your Digital Signature
                </Modal.Title>
              </Modal.Header>
              <Modal.Body className="p-4">
                {/* <Alert variant="info" className="mb-4">
                  <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                  <strong>Choose the method that works best for you:</strong> Most users find "Type My Signature" easiest to use.
                </Alert> */}

                <Tab.Container activeKey={activeSignatureTab} onSelect={setActiveSignatureTab}>
                  <Nav variant="pills" className="mb-4 justify-content-center">
                    {/* <Nav.Item>
                      <Nav.Link eventKey="type" className="px-4 py-2 mx-1">
                        <FontAwesomeIcon icon={faKeyboard} className="me-2" />
                        Type My Signature
                        <Badge bg="success" className="ms-2">
                          <FontAwesomeIcon icon={faStar} className="me-1" />
                          Recommended
                        </Badge>
                      </Nav.Link>
                    </Nav.Item> */}
                     <Nav.Item>
                      <Nav.Link eventKey="draw" className="px-4 py-2 mx-1">
                        <FontAwesomeIcon icon={faPencilAlt} className="me-2" />
                        Draw Signature
                        <Badge bg="warning" className="ms-2">Recommended</Badge>
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="upload" className="px-4 py-2 mx-1">
                        <FontAwesomeIcon icon={faUpload} className="me-2" />
                        Upload Signature
                      </Nav.Link>
                    </Nav.Item>
                   
                  </Nav>

                  <Tab.Content>
                    {/* Type Signature Tab */}
                    {/* <Tab.Pane eventKey="type">
                      <Alert variant="success" className="mb-3">
                        <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                        <strong>Easy & Professional:</strong> Type your name and choose a style. Perfect for most users!
                      </Alert>
                      
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">Your Name</Form.Label>
                            <Form.Control
                              type="text"
                              value={typedSignature.text}
                              onChange={(e) => setTypedSignature(prev => ({...prev, text: e.target.value}))}
                              placeholder="Enter your full name"
                              size="lg"
                            />
                          </Form.Group>
                          
                          <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">Signature Style</Form.Label>
                            <Form.Select
                              value={typedSignature.font}
                              onChange={(e) => setTypedSignature(prev => ({...prev, font: e.target.value}))}
                              size="lg"
                            >
                              {signatureFonts.map(font => (
                                <option key={font.value} value={font.value}>
                                  {font.name}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                          
                          <Row>
                            <Col>
                              <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">Size</Form.Label>
                                <Form.Range
                                  min="18"
                                  max="36"
                                  value={typedSignature.size}
                                  onChange={(e) => setTypedSignature(prev => ({...prev, size: parseInt(e.target.value)}))}
                                />
                                <small className="text-muted">{typedSignature.size}px</small>
                              </Form.Group>
                            </Col>
                            <Col>
                              <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">Style</Form.Label>
                                <Form.Select
                                  value={typedSignature.style}
                                  onChange={(e) => setTypedSignature(prev => ({...prev, style: e.target.value}))}
                                >
                                  <option value="normal">Normal</option>
                                  <option value="italic">Italic</option>
                                </Form.Select>
                              </Form.Group>
                            </Col>
                          </Row>
                        </Col>
                        
                        <Col md={6}>
                          <div className="text-center">
                            <Form.Label className="fw-semibold d-block mb-3">Preview</Form.Label>
                            <div 
                              className="border rounded p-4 bg-light"
                              style={{ minHeight: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              {signaturePreview && activeSignatureTab === "type" ? (
                                <img 
                                  src={signaturePreview} 
                                  alt="Signature preview"
                                  style={{ maxWidth: '100%', maxHeight: '120px' }}
                                />
                              ) : (
                                <div className="text-muted">
                                  <FontAwesomeIcon icon={faSignature} size="2x" className="mb-2" />
                                  <div>Your signature will appear here</div>
                                </div>
                              )}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Tab.Pane> */}
 
                    {/* Draw Signature Tab */}
                    <Tab.Pane eventKey="draw">
                      {/* <Alert variant="warning" className="mb-3">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                        <strong>Advanced Option:</strong> Drawing with a mouse can be difficult. Consider using "Type My Signature" for better results.
                      </Alert> */}
                      
                      <div className="text-center mb-3">
                        <small className="text-muted">
                          Draw your signature using your mouse or touchpad. 
                          <br />
                          <span className="text-info">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                            Your signature will be saved with a white background for better visibility.
                          </span>
                        </small>
                      </div>
                      
                      <div className="text-center">
                        <div 
                          className="border rounded mx-auto mb-3" 
                          style={{ 
                            border: "2px dashed #dee2e6", 
                            borderRadius: "12px",
                            backgroundColor: "white",
                            display: "inline-block",
                            padding: "10px"
                          }}
                        >
                          <SignaturePad
                            ref={sigCanvas}
                            canvasProps={{
                              width: 500,
                              height: 200,
                              style: { 
                                borderRadius: "8px",
                                backgroundColor: "white",
                                border: "1px solid #e9ecef"
                              }
                            }}
                          />
                        </div>
                        
                        <div className="mb-3">
                          <Button 
                            variant="outline-secondary" 
                            onClick={clear}
                            className="me-2"
                          >
                            <FontAwesomeIcon icon={faUndo} className="me-2" />
                            Clear & Retry
                          </Button>
                          <Button 
                            variant="outline-primary" 
                            onClick={previewDrawnSignature}
                            className="me-2"
                          >
                            <FontAwesomeIcon icon={faEye} className="me-2" />
                            Preview
                          </Button>
                        </div>
                        
                        {/* Preview for drawn signature */}
                        {activeSignatureTab === "draw" && signaturePreview && (
                          <div className="mb-3">
                            <div className="text-center">
                              <small className="text-muted d-block mb-2">Preview (with white background):</small>
                              <div 
                                className="border rounded mx-auto p-3" 
                                style={{ 
                                  backgroundColor: "white",
                                  display: "inline-block",
                                  maxWidth: "300px"
                                }}
                              >
                                <img 
                                  src={signaturePreview} 
                                  alt="Signature Preview" 
                                  style={{ 
                                    maxWidth: "100%", 
                                    height: "auto",
                                    border: "1px solid #e9ecef",
                                    borderRadius: "4px"
                                  }} 
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <Alert variant="light" className="small">
                        <strong>Drawing Tips:</strong>
                        <ul className="mb-0 mt-2">
                          <li>Write slowly and steadily</li>
                          <li>Use smooth, continuous strokes</li>
                          <li>If using a laptop, try using an external mouse</li>
                          <li>Consider using a tablet or phone for better results</li>
                        </ul>
                      </Alert>
                    </Tab.Pane>


                    {/* Upload Signature Tab */}
                    <Tab.Pane eventKey="upload">
                      <Alert variant="info" className="mb-3">
                        <FontAwesomeIcon icon={faImage} className="me-2" />
                        <strong>Use Your Real Signature:</strong> Sign on paper, take a photo or scan it, then upload here. 
                        The background will be automatically removed!
                      </Alert>
                      
                      <Row>
                        <Col md={6}>
                          <div className="text-center">
                            <div 
                              className="border-dashed border-2 rounded p-4 mb-3"
                              style={{ 
                                borderStyle: 'dashed',
                                borderColor: '#dee2e6',
                                backgroundColor: '#f8f9fa',
                                cursor: 'pointer'
                              }}
                              onClick={() => fileInputRef.current?.click()}
                            >
                              <FontAwesomeIcon icon={faUpload} size="3x" className="text-muted mb-3" />
                              <div className="mb-2">
                                <strong>Click to upload your signature</strong>
                              </div>
                              <div className="text-muted">
                                Supports: JPG, PNG, GIF files
                              </div>
                            </div>
                            
                            <input
                              type="file"
                              ref={fileInputRef}
                              onChange={handleFileUpload}
                              accept="image/*"
                              style={{ display: 'none' }}
                            />
                            
                            <Button 
                              variant="outline-primary" 
                              onClick={() => fileInputRef.current?.click()}
                              className="mb-3"
                            >
                              <FontAwesomeIcon icon={faUpload} className="me-2" />
                              Choose File
                            </Button>
                            
                            <div className="mb-3">
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setShowAdvancedUpload(!showAdvancedUpload)}
                              >
                                <FontAwesomeIcon icon={faCrop} className="me-2" />
                                {showAdvancedUpload ? 'Hide' : 'Show'} Advanced Options
                              </Button>
                            </div>
                            
                            {showAdvancedUpload && (
                              <div className="border rounded p-3 bg-light mb-3">
                                <Form.Group className="mb-3">
                                  <Form.Label className="small fw-semibold">
                                    Background Removal Strength
                                  </Form.Label>
                                  <Form.Range
                                    min="10"
                                    max="80"
                                    value={backgroundRemovalStrength}
                                    onChange={(e) => setBackgroundRemovalStrength(parseInt(e.target.value))}
                                  />
                                  <div className="d-flex justify-content-between">
                                    <small className="text-muted">Less removal</small>
                                    <small className="text-muted">More removal</small>
                                  </div>
                                  <small className="text-muted d-block mt-1">
                                    Current: {backgroundRemovalStrength}% - Adjust if background isn't fully removed
                                  </small>
                                </Form.Group>
                                
                                {uploadedImage && uploadedImage !== 'loading' && (
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={reprocessImage}
                                    className="w-100"
                                  >
                                    <FontAwesomeIcon icon={faRedo} className="me-2" />
                                    Apply New Settings
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                          
                          <Alert variant="light" className="small">
                            <strong>Tips for best results:</strong>
                            <ul className="mb-0 mt-2">
                              <li>Sign with a dark pen on white paper</li>
                              <li>Take photo in good lighting</li>
                              <li>Keep signature centered in the image</li>
                              <li>Avoid shadows or glare</li>
                              <li>White/light backgrounds will be automatically removed</li>
                              <li>Dark signature ink will be enhanced for clarity</li>
                            </ul>
                          </Alert>
                        </Col>
                        
                        <Col md={6}>
                          <div className="text-center">
                            <Form.Label className="fw-semibold d-block mb-3">Preview</Form.Label>
                            <div 
                              className="border rounded p-4 bg-light"
                              style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              {uploadedImage === 'loading' ? (
                                <div className="text-center">
                                  <Spinner animation="border" className="mb-2" />
                                  <div className="text-muted">Processing image...</div>
                                </div>
                              ) : uploadedImage ? (
                                <div className="text-center">
                                  <img 
                                    src={uploadedImage} 
                                    alt="Uploaded signature"
                                    style={{ 
                                      maxWidth: '100%', 
                                      maxHeight: '180px',
                                      border: '1px solid #dee2e6',
                                      borderRadius: '4px'
                                    }}
                                  />
                                  <div className="mt-2">
                                    <small className="text-success">
                                      <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                      Background removed & optimized for documents
                                    </small>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-muted text-center">
                                  <FontAwesomeIcon icon={faImage} size="2x" className="mb-2" />
                                  <div>Upload an image to see preview</div>
                                </div>
                              )}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Tab.Pane>

                   
                  </Tab.Content>
                </Tab.Container>
              </Modal.Body>
              <Modal.Footer className="justify-content-center">
                <Button 
                  variant="success" 
                  onClick={save}
                  size="lg"
                  className="px-4 me-2"
                >
                  <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                  Save My Signature
                </Button>
                <Button 
                  variant="outline-danger" 
                  onClick={toggle}
                  size="lg"
                  className="px-4"
                >
                  Cancel
                </Button>
              </Modal.Footer>
            </Modal>
            
            {/* Save Button */}
            <div className="text-center mt-5 pt-4 border-top">
              <Button 
                type="submit" 
                variant="primary" 
                disabled={isLoading}
                size="lg"
                className="px-5 py-3"
                style={{ borderRadius: '10px', fontSize: '1.1rem' }}
              >
                {isLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving Your Profile...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                    Save My Profile
                  </>
                )}
              </Button>
              
              {hasUnsavedChanges && (
                <div className="mt-2">
                  <small className="text-warning">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                    You have unsaved changes
                  </small>
                </div>
              )}
            </div>
            
            {/* Two-Factor Authentication (2FA) Section */}
            <div className="mt-5 pt-4 border-top">
              <h6 className="text-primary mb-3 border-bottom pb-2 d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div>
                  <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                  Two-Factor Authentication (2FA) Security
                </div>
                {user?.totp_enabled ? (
                  <Badge bg="success" className="px-3 py-2 text-wrap">
                    <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                    2FA Enabled (Google Authenticator / Authy)
                  </Badge>
                ) : (
                  <Badge bg="warning" text="dark" className="px-3 py-2 text-wrap">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                    2FA Disabled
                  </Badge>
                )}
              </h6>

              <Card className="border shadow-sm p-4 bg-light">
                <Row className="align-items-center">
                  <Col md={8}>
                    <h6 className="fw-bold mb-1">
                      <FontAwesomeIcon icon={faKey} className="me-2 text-primary" />
                      Authenticator App (TOTP)
                    </h6>
                    <p className="text-muted small mb-0">
                      Protect your account by generating time-based one-time passcodes using Google Authenticator, Authy, or Microsoft Authenticator app on your phone during login.
                    </p>
                  </Col>
                  <Col md={4} className="text-md-end mt-3 mt-md-0">
                    {user?.totp_enabled ? (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={handleDisableMfa}
                        disabled={resettingMfa}
                      >
                        {resettingMfa ? (
                          <><Spinner animation="border" size="sm" className="me-1" /> Disabling...</>
                        ) : (
                          "Disable 2FA"
                        )}
                      </Button>
                    ) : (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setShowMfaModal(true)}
                      >
                        <FontAwesomeIcon icon={faShieldAlt} className="me-1" />
                        Set Up 2FA Now
                      </Button>
                    )}
                  </Col>
                </Row>
              </Card>

              <MFASetupModal isOpen={showMfaModal} onClose={() => setShowMfaModal(false)} />
            </div>

            {/* Password Change Section */}
            <div className="mt-5 pt-4 border-top">
              <ProfileUpdatePassword form={form} setForm={setForm} />
            </div>
          </Form>
        </Tab.Pane>

        <Tab.Pane eventKey="notifications">
          <NotificationPreferences user={user} />
        </Tab.Pane>
      </Tab.Content>
    </Tab.Container>
        </Card.Body>
      </Card>
    </Container>
  );
}
